import React from "react";
import {durationToHuman, formatters} from "@mediaspace/shared/utils";
import styled from "@emotion/styled";
import {alpha, Box} from "@mediaspace/shared/styled";
import {Thumbnail, TruncateWithTooltip} from "@mediaspace/shared/ui";
import {ButtonClickAnalyticsType} from "@mediaspace/shared/types/ButtonClickAnalyticsType";
import {useButtonAnalytics, useEntryUrl} from "@mediaspace/hooks";
import {KalturaEntryType} from "kaltura-typescript-client/api/types";
import {useConfig} from "@mediaspace/shared/data-kms-config";
import {Link} from "@kaltura/ds-react-components";
import { translate } from "@mediaspace/shared/utils";

export interface GridItemProps {
    name: string;
    image?: string;
    duration?: number;
    entryId: string;
    entryType: KalturaEntryType;
    categoryId?: number;
    playlistId?: string;
    analyticsName?: string;
}

const StyledGridItem = styled(Box)(({ theme }) => ({
    backgroundColor: theme.kaltura.palette.tone7,
    borderRadius: theme.kaltura.shape.roundness1,
    overflow: "hidden",
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        marginTop: theme.spacing(2)
    },
}));

const ThumbnailWrapper = styled(Box)(({ theme }) => ({
    position: "relative",
    borderTopLeftRadius: theme.kaltura.shape.roundness1,
    borderTopRightRadius: theme.kaltura.shape.roundness1
}));

const StyledThumbnail = styled(Thumbnail)(({ theme }) => ({
    "& img": {
        borderTopLeftRadius: theme.kaltura.shape.roundness1,
        borderTopRightRadius: theme.kaltura.shape.roundness1,
        width: "100%",
        aspectRatio: "16/9"
    }
}));

const StyledDuration = styled.span(({ theme }) => ({
    backgroundColor: alpha(theme.palette.common.black, 0.6),
    borderRadius: theme.kaltura.shape.roundness1,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(16),
    fontWeight: theme.kaltura.typography.fontWeightBold,
    padding: "4px 6px",
    left: theme.spacing(1),
    bottom: theme.spacing(1),
    position: "absolute",
    color: theme.palette.common.white,
}));

const StyledLink = styled(Link)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.kaltura.typography.fontWeightBold,
    color: theme.kaltura.palette.tone1,
    textDecoration: "none",
    display: "block",
}));

const StyledNameContainer = styled.div(({ theme }) => ({
    height: 36,
    padding: theme.spacing(2),
    boxSizing: "content-box",
}));

const baseClass = "kms-ds-media-grid-item";

/**
 * grid item
 */
const GridItem = (props: GridItemProps) => {

    const {
        name,
        image = "",
        duration,
        entryId,
        entryType,
        categoryId,
        playlistId,
        analyticsName
    } = props;

    const config = useConfig();

    const linkUrl = useEntryUrl({
        entry: {id: entryId, name, type: entryType}, categoryId, playlistId
    });

    const openInNewTab = entryType === KalturaEntryType.document && !config.application.isKAF;

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = () => {
        if (analyticsName) {
            sendButtonAnalytics(analyticsName, ButtonClickAnalyticsType.THUMBNAIL);
        }
    }

    return (
        <StyledGridItem
            className={`${baseClass}`}
            onClick={sendAnalytics}
        >
            <ThumbnailWrapper className={`${baseClass}-thumbnail`}>
                <StyledThumbnail
                    thumbnailUrl={image}
                    linkUrl={linkUrl}
                    ariaLabelText={translate("Entry thumbnail. Go to %1 with duration: %2", [name, durationToHuman(duration)])}
                    openInNewTab={openInNewTab}
                />
                {!!duration && (
                    <StyledDuration
                        aria-label={durationToHuman(duration)}
                        role={"text"}
                        className={`${baseClass}-duration`}
                    >
                        {formatters.formatSecondsToTime(duration)}
                    </StyledDuration>
                )}
            </ThumbnailWrapper>
            <StyledNameContainer className={`${baseClass}-content-container`}>
                <TruncateWithTooltip text={name} lines={2}>
                    <StyledLink
                        className={`${baseClass}-content`}
                        href={linkUrl}
                        target={openInNewTab ? "_blank" : ""}
                        aria-label={translate("go to %1", [name])}
                    >
                        {name}
                    </StyledLink>
                </TruncateWithTooltip>
            </StyledNameContainer>
        </StyledGridItem>
    );
};

export default GridItem;
